/**
 * Display-URL resolution for capture thumbnails.
 *
 * The captureSheet doc stores only a per-capture `thumbPrefix` + `thumbsReady`
 * flag (not 144 URLs/capture — see firestore-shapes.md). This helper templates
 * the display URL off that prefix, branching emulator vs production:
 *
 *   emulator   — the GCS download endpoint (bypasses Storage rules, which deny
 *                Firebase-style reads in dev). Verified round-trippable.
 *   production — the public object URL (thumbs are uploaded public-read).
 *
 * Until the bridge has uploaded a capture's thumbs (thumbsReady false), the
 * gray-box placeholder from cameras.js stands in.
 */
import { pad4, thumbDataUri } from "./cameras.js";

const BUCKET = "nyc-suite.firebasestorage.app";
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === "true";
const EMU_ORIGIN = "http://127.0.0.1:9199";

/** Full display URL for one uploaded camera thumb. */
export function thumbObjectUrl(thumbPrefix, camera) {
  const path = `${thumbPrefix}/cam_${pad4(camera)}.jpg`;
  if (USE_EMULATORS) {
    return `${EMU_ORIGIN}/download/storage/v1/b/${BUCKET}/o/${encodeURIComponent(path)}?alt=media`;
  }
  return `https://storage.googleapis.com/${BUCKET}/${path}`;
}

/**
 * Best display URL for a camera: the real uploaded thumb when the bridge has it,
 * otherwise the deterministic gray-box placeholder (rotation baked in for the
 * placeholder; real thumbs render unrotated this slice — rotation-on-real-thumbs
 * is deferred, see firestore-shapes.md).
 */
export function cameraThumbUrl(capture, camera, rotation = 0) {
  if (capture?.thumbsReady && capture?.thumbPrefix) {
    return thumbObjectUrl(capture.thumbPrefix, camera);
  }
  return thumbDataUri(camera, rotation);
}

/**
 * Degrees to CSS-rotate a thumbnail at display time (F6a). Real uploaded thumbs
 * are unrotated bytes, so the rotation map is applied in CSS; placeholders bake
 * rotation into the SVG already, so they get 0 here (no double-rotate).
 */
export function thumbCssRotation(capture, rotation = 0) {
  return capture?.thumbsReady && capture?.thumbPrefix ? rotation : 0;
}
